import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";


function AshramCulture() {
  const TARGET_CATEGORY_ID = 76

  const navigate = useNavigate()

  const [infoItems, setInfoItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const wpBase = useMemo(() => {
    const raw = (process.env.REACT_APP_NEXT_ASHRAM_APP_URL || "https://ashramapp.srmd.org").toString()
    return raw.replace(/\/+$/, "")
  }, [])

  const sanitizeWpHtml = (html) => {
    // Best-effort sanitization:
    // - remove <script> tags
    // - keep the rest of WP formatting
    const raw = (html ?? "").toString()
    if (!raw) return ""
    return raw.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
  }

  const sanitizeWpText = (value) => {
    const raw = (value ?? "").toString()
    return raw
      .replace(/<[^>]*>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .trim()
  }

  const pickInfoImage = (post) => {
    // Try common fields used in this WP instance:
    // - ACF images/gallery
    // - Featured media (via _embed)
    // If none, return empty string.
    const acf = post?.acf
    const imgFromAcf =
      acf?.images?.[0]
      ?? acf?.image
      ?? acf?.thumbnail
      ?? acf?.slider_gallery?.[0]
      ?? acf?.vertical_gallery?.[0]

    const imgFromFeatured = post?._embedded?.['wp:featuredmedia']?.[0]?.source_url
    return (imgFromAcf || imgFromFeatured || "").toString()
  }

  const fetchCultureContent = useCallback(async () => {
    try {
      setLoading(true)
      setError("")

      // Information -> show items where categories includes 76
      const infoUrl = `${wpBase}/wp-json/wp/v2/information?_embed&_fields=id,title,content,acf,_links.wp:featuredmedia,_embedded.wp:featuredmedia,categories&per_page=100&status=publish`
      const infoRes = await fetch(infoUrl)
      if (!infoRes.ok) throw new Error(`Information API failed: ${infoRes.status}`)
      const infoJson = await infoRes.json()
      const rows = Array.isArray(infoJson) ? infoJson : []
      const filtered = rows.filter((p) => Array.isArray(p?.categories) && p.categories.includes(TARGET_CATEGORY_ID))

      const mapped = filtered.map((p) => {
        const postTitle = sanitizeWpText(p?.title?.rendered)
        const contentHtml = sanitizeWpHtml(p?.content?.rendered)
        return {
          id: p?.id,
          title: postTitle,
          contentHtml,
          imageUrl: pickInfoImage(p),
        }
      })

      // Optional: stable ordering if WP provides ACF order_by
      mapped.sort((a, b) => {
        const ao = Number(a?.order_by)
        const bo = Number(b?.order_by)
        if (Number.isFinite(ao) && Number.isFinite(bo) && ao !== bo) return ao - bo
        return (a?.title ?? '').localeCompare(b?.title ?? '')
      })

      setInfoItems(mapped)
    } catch (e) {
      console.log(e)
      setError("Unable to load content right now.")
    } finally {
      setLoading(false)
    }
  }, [wpBase])

  useEffect(() => {
    let alive = true
    ;(async () => {
      if (!alive) return
      await fetchCultureContent()
    })()
    return () => {
      alive = false
    }
  }, [fetchCultureContent])

  return (
    <div className="bg-[#fedcdb]">
      {/* WP-driven content for category 76 */}
      <section className="bg-gradient-to-r from-[#f5eadc] min-h-screen via-[#f5eadc] to-[#f5eadc] py-10 px-2 md:px-20">

        {/* Header (in-app style) */}
        <div className="max-w-5xl mx-auto mb-6">
          <button
            type="button"
            onClick={() => {
              // Prefer router back; fallback to dashboard if user landed directly.
              if (window.history.length > 1) navigate(-1)
              else navigate('/visitorPortal')
            }}
            className="flex items-center gap-2 font-bold text-sm text-rose-900"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-rose-900">Ashram Culture</h2>
          <p className="mt-1 text-sm text-rose-900/70">Values & way of life</p>
        </div>


        {loading ? (
          <p className="mt-4 text-rose-900 font-semibold min-h-screen flex items-center justify-center">Loading…</p>
        ) : error ? (
          <div className="mt-4 text-rose-900 font-semibold">
            <p>{error}</p>
            <button
              type="button"
              onClick={fetchCultureContent}
              className="mt-4 px-4 py-2 rounded-full text-xs font-bold bg-white/70 hover:bg-white"
            >
              Retry
            </button>
          </div>
        ) : infoItems.length === 0 ? (
          <p className="mt-4 text-rose-900 font-semibold">No content available.</p>
        ) : (
          <div className="mt-6 space-y-6">
            {infoItems.map((item) => (
              <article
                key={item.id}
                className=""
              >
                <div className="grid grid-cols-1 md:grid-cols-12">


                  {/* Content */}
                  <div className={"md:col-span-12"}>
                    <div className="p-5 md:p-6">
                      {item.title ? (
                        <h3 className="text-xl md:text-2xl font-extrabold text-rose-900 mb-3">
                          {item.title}
                        </h3>
                      ) : null}
                      <div className="bg-white p-5 rounded-2xl shadow overflow-hidden">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-auto rounded-xl mb-4"
                            loading="lazy"
                          />
                        ) : null}
                        <div
                          className="prose prose-rose max-w-none prose-p:leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

export default AshramCulture;
